import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  maskCep,
  maskCnpj,
  maskPhone,
} from '@/shared/lib/input-masks';
import { MaskedInput } from '@/shared/components/masked-input';
import { isValidEmail } from '@/features/nps/public-survey/partition-questions';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import { Skeleton } from '@/shared/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { CLIENT_PROFILE_OPTIONS, CLIENT_SECTOR_OPTIONS } from '../model/client-form-meta';
import { submitPublicClientDraft } from '../api/use-public-register';
import { fetchPublicCatalog } from '@/features/catalog/api/use-catalog';

const BR_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
] as const;

interface ContractSigner {
  name: string;
  email: string;
}

interface FormState {
  filledBy: string;
  legalName: string;
  cnpj: string;
  stateRegistration: string;
  name_client: string;
  zipcode: string;
  address: string;
  number: string;
  complement: string;
  city: string;
  state: string;
  email_client: string;
  phone: string;
  whatsapp: string;
  billingEmails: string;
  financialContactPhone: string;
  contractWitnessEmail: string;
  sector: string;
  type: string;
  site: string;
  pdv: string;
  website: string; // honeypot
}

const EMPTY_FORM: FormState = {
  filledBy: '',
  legalName: '',
  cnpj: '',
  stateRegistration: '',
  name_client: '',
  zipcode: '',
  address: '',
  number: '',
  complement: '',
  city: '',
  state: '',
  email_client: '',
  phone: '',
  whatsapp: '',
  billingEmails: '',
  financialContactPhone: '',
  contractWitnessEmail: '',
  sector: '',
  type: '',
  site: '',
  pdv: '',
  website: '',
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
    </div>
  );
}

/** Formulário público — mesmas infos do "Cadastrar cliente" admin + dados do contrato, sem login. Cria como rascunho inativo. */
export function PublicClientRegisterPage() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [signers, setSigners] = useState<ContractSigner[]>([{ name: '', email: '' }]);
  const [modules, setModules] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { data: catalogModules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ['public-catalog', 'module'],
    queryFn: () => fetchPublicCatalog('module'),
    staleTime: 5 * 60 * 1000,
  });
  const moduleOptions = catalogModules.map((m) => m.name);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setSigner = (index: number, field: keyof ContractSigner, value: string) =>
    setSigners((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));

  const addSigner = () => setSigners((prev) => [...prev, { name: '', email: '' }]);
  const removeSigner = (index: number) => setSigners((prev) => prev.filter((_, i) => i !== index));

  const toggleModule = (name: string, checked: boolean) =>
    setModules((prev) => (checked ? [...prev, name] : prev.filter((m) => m !== name)));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const required: Array<[string, string]> = [
      [form.filledBy, 'Responsável pelo preenchimento'],
      [form.legalName, 'Razão social'],
      [form.cnpj, 'CNPJ'],
      [form.stateRegistration, 'Inscrição estadual'],
      [form.name_client, 'Nome fantasia'],
      [form.zipcode, 'CEP'],
      [form.address, 'Endereço'],
      [form.number, 'Número'],
      [form.city, 'Cidade'],
      [form.state, 'Estado'],
      [form.email_client, 'E-mail de contato geral'],
      [form.phone, 'Telefone de contato'],
      [form.whatsapp, 'Celular WhatsApp'],
      [form.billingEmails, 'E-mail(s) de cobrança e nota fiscal'],
      [form.financialContactPhone, 'WhatsApp/telefone do responsável financeiro'],
      [form.contractWitnessEmail, 'E-mail da testemunha do contrato'],
      [form.pdv, 'Sistema de vendas (PDV)'],
    ];
    for (const [value, label] of required) {
      if (!value.trim()) {
        toast.error(`Preencha o campo: ${label}.`);
        return;
      }
    }
    if (!isValidEmail(form.email_client)) {
      toast.error('Informe um e-mail de contato geral válido.');
      return;
    }
    if (!isValidEmail(form.contractWitnessEmail)) {
      toast.error('Informe um e-mail válido para a testemunha do contrato.');
      return;
    }
    const validSigners = signers.filter((s) => s.name.trim() && s.email.trim());
    if (validSigners.length === 0) {
      toast.error('Informe pelo menos um responsável administrativo (nome e e-mail) para assinar o contrato.');
      return;
    }
    for (const s of validSigners) {
      if (!isValidEmail(s.email)) {
        toast.error(`E-mail inválido para o responsável administrativo "${s.name}".`);
        return;
      }
    }
    if (modules.length === 0) {
      toast.error('Selecione ao menos um módulo contratado.');
      return;
    }

    setSubmitting(true);
    try {
      await submitPublicClientDraft({
        filled_by: form.filledBy.trim(),
        legal_name: form.legalName.trim(),
        name_client: form.name_client.trim(),
        cnpj: form.cnpj,
        state_registration: form.stateRegistration.trim(),
        email_client: form.email_client.trim(),
        phone: form.phone,
        whatsapp: form.whatsapp,
        zipcode: form.zipcode,
        address: form.address,
        number: form.number,
        complement: form.complement,
        city: form.city,
        state: form.state,
        sector: form.sector,
        type: form.type,
        site: form.site,
        pdv: form.pdv,
        billing_emails: form.billingEmails.trim(),
        financial_contact_phone: form.financialContactPhone,
        contract_witness_email: form.contractWitnessEmail.trim(),
        contractSigners: validSigners,
        models: modules,
        contacts: [],
        website: form.website,
      });
      setSubmitted(true);
    } catch {
      toast.error('Não foi possível enviar seu cadastro. Tente novamente em alguns minutos.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="max-w-md p-8 text-center">
          <CheckCircle2 className="mx-auto size-12 text-success" />
          <h1 className="mt-4 text-xl font-semibold">Recebemos seu cadastro</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Nossa equipe vai revisar as informações e entrar em contato em breve.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <Card className="w-full max-w-3xl p-6 sm:p-8">
        <h1 className="text-xl font-semibold">Cadastro de cliente</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Preencha os dados da sua empresa e do contrato. Nossa equipe revisa e entra em contato para os
          próximos passos.
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-8">
          <section className="space-y-4">
            <Field label="Responsável pelo preenchimento" required>
              <Input value={form.filledBy} onChange={(e) => set('filledBy', e.target.value)} />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Razão social" required>
                <Input value={form.legalName} onChange={(e) => set('legalName', e.target.value)} />
              </Field>
              <Field label="Nome fantasia (marca)" required>
                <Input value={form.name_client} onChange={(e) => set('name_client', e.target.value)} />
              </Field>
              <Field label="CNPJ" required>
                <MaskedInput
                  value={form.cnpj}
                  onValueChange={(v) => set('cnpj', v)}
                  mask={maskCnpj}
                  inputMode="numeric"
                  placeholder="00.000.000/0000-00"
                />
              </Field>
              <Field label="Inscrição estadual" required>
                <Input value={form.stateRegistration} onChange={(e) => set('stateRegistration', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-sm font-semibold">Endereço</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="CEP" required>
                <MaskedInput
                  value={form.zipcode}
                  onValueChange={(v) => set('zipcode', v)}
                  mask={maskCep}
                  inputMode="numeric"
                  placeholder="00000-000"
                />
              </Field>
              <Field label="Endereço" required>
                <Input value={form.address} onChange={(e) => set('address', e.target.value)} />
              </Field>
              <Field label="Número" required>
                <Input value={form.number} onChange={(e) => set('number', e.target.value)} />
              </Field>
              <Field label="Complemento">
                <Input value={form.complement} onChange={(e) => set('complement', e.target.value)} />
              </Field>
              <Field label="Cidade" required>
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
              <Field label="Estado" required>
                <Select value={form.state || undefined} onValueChange={(v) => set('state', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    {BR_STATES.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-sm font-semibold">Contato</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-mail contato geral" required>
                <Input type="email" value={form.email_client} onChange={(e) => set('email_client', e.target.value)} />
              </Field>
              <Field label="Telefone de contato" required>
                <MaskedInput
                  value={form.phone}
                  onValueChange={(v) => set('phone', v)}
                  mask={maskPhone}
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                />
              </Field>
              <Field label="Celular WhatsApp" required>
                <MaskedInput
                  value={form.whatsapp}
                  onValueChange={(v) => set('whatsapp', v)}
                  mask={maskPhone}
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                />
              </Field>
              <Field label="Site">
                <Input type="url" placeholder="https://" value={form.site} onChange={(e) => set('site', e.target.value)} />
              </Field>
              <Field label="Setor">
                <Select value={form.sector || undefined} onValueChange={(v) => set('sector', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o setor" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_SECTOR_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Perfil">
                <Select value={form.type || undefined} onValueChange={(v) => set('type', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_PROFILE_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-sm font-semibold">Financeiro</h2>
            <p className="text-xs text-muted-foreground">
              E-mail(s) para envio de cobrança e nota fiscal — se houver mais de um, separe por vírgula.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="E-mail(s) de cobrança e nota fiscal" required>
                <Input value={form.billingEmails} onChange={(e) => set('billingEmails', e.target.value)} />
              </Field>
              <Field label="WhatsApp/telefone do responsável financeiro" required>
                <MaskedInput
                  value={form.financialContactPhone}
                  onValueChange={(v) => set('financialContactPhone', v)}
                  mask={maskPhone}
                  type="tel"
                  inputMode="tel"
                  placeholder="(00) 00000-0000"
                />
              </Field>
            </div>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-sm font-semibold">Contrato</h2>
            <p className="text-xs text-muted-foreground">
              Nome(s) e e-mail(s) do(s) responsável(is) administrativo(s) de acordo com o contrato social —
              é pra eles que vai o contrato pra assinatura. O CPF é exigido só no momento de assinar, não
              precisa informar aqui.
            </p>
            <div className="space-y-3">
              {signers.map((signer, index) => (
                <div key={index} className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                  <Field label={`Responsável administrativo ${index + 1} — nome`} required={index === 0}>
                    <Input value={signer.name} onChange={(e) => setSigner(index, 'name', e.target.value)} />
                  </Field>
                  <Field label="E-mail" required={index === 0}>
                    <Input
                      type="email"
                      value={signer.email}
                      onChange={(e) => setSigner(index, 'email', e.target.value)}
                    />
                  </Field>
                  {signers.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="justify-self-end text-destructive"
                      onClick={() => removeSigner(index)}
                      aria-label="Remover responsável"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </div>
              ))}
              <Button type="button" variant="secondary" size="sm" onClick={addSigner}>
                <Plus className="size-4" /> Adicionar outro responsável
              </Button>
            </div>

            <Field label="E-mail para testemunha do contrato" required>
              <Input
                type="email"
                value={form.contractWitnessEmail}
                onChange={(e) => set('contractWitnessEmail', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Um e-mail que não faça parte do quadro societário, só pra assinar como testemunha.
              </p>
            </Field>
          </section>

          <section className="space-y-4 border-t border-border pt-6">
            <h2 className="text-sm font-semibold">
              Módulos contratados <span className="text-destructive">*</span>
            </h2>
            <p className="text-xs text-muted-foreground">De acordo com a proposta enviada.</p>
            {modulesLoading ? (
              <Skeleton className="h-24 rounded-xl" />
            ) : moduleOptions.length === 0 ? (
              <p className="rounded-xl border border-dashed border-border/60 px-4 py-6 text-center text-sm text-muted-foreground">
                Nenhum módulo disponível no momento. Entre em contato com a fixe.
              </p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {moduleOptions.map((product) => (
                  <label
                    key={product}
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/50 bg-background/40 px-3 py-2.5"
                  >
                    <Checkbox
                      checked={modules.includes(product)}
                      onCheckedChange={(value) => toggleModule(product, value === true)}
                    />
                    <span className="text-sm">{product}</span>
                  </label>
                ))}
              </div>
            )}

            <Field label="Qual é o sistema de vendas (PDV) utilizado?" required>
              <Input value={form.pdv} onChange={(e) => set('pdv', e.target.value)} placeholder="Ex.: loja física, e-commerce…" />
            </Field>
          </section>

          {/* Honeypot — invisível pra humanos, bots costumam preencher todo campo. */}
          <div className="sr-only" aria-hidden="true">
            <Label htmlFor="website">Não preencha este campo</Label>
            <Input
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
            />
          </div>

          <Button type="submit" className="w-full" loading={submitting}>
            Enviar cadastro
          </Button>
        </form>
      </Card>
    </div>
  );
}
